//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { createSelector } from 'reselect';

import { isGalleryContainerType } from '@galleries/utils/galleryTypeUtils';
import { ICuralateStore } from '@models/AppState';
import { ExperienceContainerType } from '@models/ExperienceContainerType';
import { alphabetizeLocaleCompare } from '@utils/arrayUtils';

export const getIdsOfAlphabetizedGalleries = createSelector(
  (state: ICuralateStore) => state.galleries.data.galleries.byId,
  (state: ICuralateStore) => state.galleries.data.galleries.orderedIds,
  (byId, orderedIds) => {
    const nameGetter = (id) => byId[id].displayName;
    const getIdsByContainerType = (type: ExperienceContainerType) => {
      return orderedIds.filter((id) => isGalleryContainerType(byId[id], type));
    };

    // Always show the homepage, category, and product galleries first. Then
    // alphabetize the rest of them. While there should only ever be one of
    // each of these three gallery types, we ensure nothing is removed by
    // ignoring that assumption and treating all gallery types as a list.
    const homepageGalleries = getIdsByContainerType(
      ExperienceContainerType.HOMEPAGE,
    );
    const categoryGalleries = getIdsByContainerType(
      ExperienceContainerType.CATEGORY,
    );
    const productGalleries = getIdsByContainerType(
      ExperienceContainerType.PRODUCT,
    );

    const firstGalleries = [
      ...alphabetizeLocaleCompare(homepageGalleries, nameGetter),
      ...alphabetizeLocaleCompare(categoryGalleries, nameGetter),
      ...alphabetizeLocaleCompare(productGalleries, nameGetter),
    ];

    const otherGalleries = orderedIds.filter(
      (id) => !firstGalleries.includes(id),
    );

    return [
      ...firstGalleries,
      ...alphabetizeLocaleCompare(otherGalleries, nameGetter),
    ];
  },
);

export default getIdsOfAlphabetizedGalleries;
